import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useAppStore from '@/store/useAppStore'

/**
 * Query-key roots owned by the dashboard.
 *
 * `transactions` covers the chart feed — useGetCashflowPeriods namespaces
 * itself under it so the table's own mutation hooks already refresh the grid.
 * The rest are dashboard-only endpoints on `staleTime: Infinity`, which is
 * why editing in the table used to leave them showing yesterday's numbers.
 */
const DASHBOARD_QUERY_ROOTS = new Set([
  'transactions',
  'forecast-score',
  'month-to-date-score',
  'seasonal-insights',
  'status',
  'available-to-spend',
  'todo-summary',
])

/**
 * Refetches every dashboard feed and records when that happened.
 *
 * Returns a stable `refresh` so it can sit in an effect's dependency list
 * without re-firing.
 */
export default function useDashboardRefresh() {
  const queryClient = useQueryClient()
  const setDashboardRefreshedAt = useAppStore(
    (state) => state.setDashboardRefreshedAt,
  )

  return React.useCallback(async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => DASHBOARD_QUERY_ROOTS.has(query.queryKey?.[0] as string),
    })
    setDashboardRefreshedAt(Date.now())
  }, [queryClient, setDashboardRefreshedAt])
}
